
import React from 'react';
import { Trophy, Lightbulb, Puzzle } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const ChallengesTab = () => {
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium flex items-center">
        <Trophy className="mr-2 h-5 w-5" /> Challenge System
      </h3>
      <p>
        Throughout your career, you'll face challenges that require strategic decision making.
        These represent real-world situations in the Bitcoin mining industry that test
        your knowledge and skills.
      </p>
      
      <div className="border rounded-md p-4 bg-muted/20">
        <h4 className="font-medium mb-2">Challenge Structure</h4>
        <ol className="list-decimal list-inside space-y-2 text-sm">
          <li>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="font-medium cursor-help">Challenge Presentation</span>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p className="w-[200px]">Challenges reflect real industry scenarios</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            &nbsp;- You'll be presented with a scenario and several possible responses
          </li>
          <li>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="font-medium cursor-help">Decision Making</span>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p className="w-[200px]">Each decision has risk/reward trade-offs</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            &nbsp;- Each option has different effects on your KPIs and may unlock specific skills
          </li>
          <li>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="font-medium cursor-help">Outcome</span>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p className="w-[200px]">Success chances increase with relevant skills</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            &nbsp;- After choosing, you'll see the immediate effects on your career and KPIs
          </li>
          <li>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="font-medium cursor-help">Experience Gain</span>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p className="w-[200px]">Higher difficulty challenges award more experience</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            &nbsp;- Each challenge awards experience points based on its difficulty
          </li>
        </ol>
        
        <div className="mt-4">
          <h4 className="font-medium mb-2">Challenge Frequency</h4>
          <p className="text-sm">
            Challenges appear randomly as you advance time in the simulation:
          </p>
          <ul className="list-disc list-inside space-y-1 text-sm mt-2">
            <li>Junior level: ~3 challenges per year</li>
            <li>Mid-level: ~4 challenges per year</li>
            <li>Senior level: ~5 challenges per year</li>
            <li>Leadership: 6-7 challenges per year</li>
          </ul>
          <p className="text-xs text-muted-foreground mt-2">
            Note: Challenge frequency is affected by your Game Speed setting
          </p>
        </div>
      </div>
      
      <div className="border-l-4 border-primary p-4 bg-primary/5">
        <h4 className="font-medium flex items-center">
          <Lightbulb className="mr-2 h-4 w-4 text-primary" /> Strategic Choices
        </h4>
        <p className="text-sm">
          When facing challenges, consider your long-term career goals. Some options
          provide immediate KPI boosts while others unlock valuable skills that
          provide permanent benefits. Higher difficulty challenges provide more
          experience but may have riskier outcomes!
        </p>
      </div>
      
      <div className="p-4 border rounded-md bg-muted/10">
        <h4 className="font-medium flex items-center mb-2">
          <Puzzle className="mr-2 h-4 w-4 text-web3-green" /> Special Events
        </h4>
        <p className="text-sm mb-2">
          Occasionally, industry-wide special events will occur:
        </p>
        <ul className="space-y-2 text-sm list-disc list-inside">
          <li>Bitcoin halvings (approximately every 4 years)</li>
          <li>Major regulatory changes</li>
          <li>Technology breakthroughs</li>
          <li>Market opportunities</li>
        </ul>
        <p className="text-xs text-muted-foreground mt-2">
          These events often present unique challenges and opportunities!
        </p>
      </div>
    </div>
  );
};

export default ChallengesTab;
